/**
 * PendingRegistrationsPage - Admin page for approving/rejecting user registrations
 * User Story 4 (T157-T159)
 */

import React, { useState, useEffect } from 'react';
import { getPendingUsers, approveUser, rejectUser } from '../../api/admin';
import { User } from '../../types/user';
import Card from '../../components/Card';
import Button from '../../components/Button';
import TextArea from '../../components/TextArea';
import EmptyStateCard from '../../components/EmptyStateCard';

const PendingRegistrationsPage: React.FC = () => {
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  useEffect(() => {
    loadPendingUsers();
  }, []);

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const users = await getPendingUsers();
      setPendingUsers(users);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error loading pending users';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (user: User) => {
    try {
      await approveUser(user.id);
      setSuccessMessage(`${user.username} approved successfully`);
      // Refresh the list
      await loadPendingUsers();
      // Clear message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve user';
      setError(errorMessage);
    }
  };

  const handleRejectClick = (user: User) => {
    setSelectedUser(user);
    setRejectionReason('');
    setReasonError(null);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setReasonError('Reason is required');
      return;
    }

    if (!selectedUser) return;

    try {
      await rejectUser(selectedUser.id, rejectionReason);
      setSuccessMessage(`${selectedUser.username} rejected successfully`);
      setShowRejectModal(false);
      setSelectedUser(null);
      setRejectionReason('');
      // Refresh the list
      await loadPendingUsers();
      // Clear message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reject user';
      setError(errorMessage);
    }
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setSelectedUser(null);
    setRejectionReason('');
    setReasonError(null);
  };

  if (loading) {
    return (
      <div
        data-testid="pending-registrations-page"
        className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8"
      >
        <div className="text-center">
          <div className="spinner"></div>
          <p className="mt-4">Loading pending registrations...</p>
        </div>
      </div>
    );
  }

  return (
    /* The testid marks this page as reached; App.test asserts on its ABSENCE for a non-admin, so
       without it those guard tests would pass against any page at all. */
    <div
      data-testid="pending-registrations-page"
      className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8"
    >
      <h1 className="text-3xl font-bold mb-6">Pending Registrations</h1>

      {error && (
        <div className="p-4 bg-error/10 dark:bg-error/20 border border-error/30 rounded-lg text-error mb-4">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-success/10 dark:bg-success/20 border border-success/30 rounded-lg text-success mb-4">
          {successMessage}
        </div>
      )}

      {pendingUsers.length === 0 ? (
        <EmptyStateCard
          icon="✅"
          title="No pending registrations"
          message="There are no registration requests awaiting your review."
        />
      ) : (
        <Card>
          <Card.Body>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Username</th>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Full Name</th>
                    <th className="text-left py-3 px-4">Registered</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-3 px-4">{user.username}</td>
                      <td className="py-3 px-4">{user.email}</td>
                      <td className="py-3 px-4">{user.full_name || '-'}</td>
                      <td className="py-3 px-4">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleApprove(user)}
                          className="mr-2"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRejectClick(user)}
                        >
                          Reject
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Reject Registration</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              Please provide a reason for rejecting <strong>{selectedUser?.username}</strong>'s registration:
            </p>
            <div className="mb-4">
              <TextArea
                id="rejection-reason"
                label="Reason for Rejection"
                required
                rows={3}
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  setReasonError(null);
                }}
                placeholder="Enter rejection reason..."
              />
              {reasonError && (
                <p className="text-error text-sm mt-1">{reasonError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleRejectCancel}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleRejectConfirm}>
                Confirm Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingRegistrationsPage;
